import axios from 'axios';
import { acquireToken } from './tokenHelper';
import { reportError, reportPerf } from './errorReporter';
import type {
  Workflow,
  Agent,
  Execution,
  UserProfile,
  WorkflowListResponse,
  AgentListResponse,
  AgentVersion,
  TestCase,
  TestRun,
  TestSuiteRun,
  ModelDeployment,
} from '../types';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Bearer token interceptor — uses the shared MSAL singleton (same instance as MsalProvider)
api.interceptors.request.use(async (config) => {
  const token = await acquireToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 response interceptor — force-refresh token once, then reject
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const token = await acquireToken(true);
      if (token) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

// Track slow API calls (>3s) and 5xx errors
api.interceptors.request.use((config) => {
  (config as any)._startTime = Date.now();
  return config;
});

api.interceptors.response.use(
  (response) => {
    const start = (response.config as any)._startTime;
    if (start) {
      const durationMs = Date.now() - start;
      if (durationMs > 3000) {
        reportPerf('SlowApiCall', durationMs, { endpoint: response.config.url || '' });
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status >= 500) {
      reportError(
        new Error(`API ${error.response.status}: ${error.config?.url || 'unknown'}`),
        { source: 'api-interceptor', status: String(error.response.status) },
      );
    }
    return Promise.reject(error);
  },
);

// Workflow API
export const workflowApi = {
  list: async (params?: { page?: number; pageSize?: number; isActive?: boolean }) => {
    const { data } = await api.get<WorkflowListResponse>('/workflows', { params });
    return data;
  },

  get: async (id: string) => {
    const { data } = await api.get<Workflow>(`/workflows/${id}`);
    return data;
  },

  create: async (workflow: Omit<Workflow, 'id' | 'createdAt' | 'updatedAt' | 'version'>) => {
    const { data } = await api.post<Workflow>('/workflows', workflow);
    return data;
  },

  update: async (id: string, updates: Partial<Workflow>) => {
    const { data } = await api.put<Workflow>(`/workflows/${id}`, updates);
    return data;
  },

  delete: async (id: string) => {
    await api.delete(`/workflows/${id}`);
  },

  execute: async (workflowId: string, input?: Record<string, unknown>) => {
    const { data } = await api.post<Execution>(`/workflows/${workflowId}/execute`, { input });
    return data;
  },
};

// Execution API
export const executionApi = {
  list: async (params?: { status?: string; page?: number; pageSize?: number }) => {
    const { data } = await api.get<{ executions: Execution[]; page: number; pageSize: number }>('/executions', { params });
    return data;
  },

  get: async (id: string) => {
    const { data } = await api.get<Execution>(`/executions/${id}`);
    return data;
  },

  delete: async (id: string) => {
    await api.delete(`/executions/${id}`);
  },
};

// Agent API
export const agentApi = {
  list: async (params?: { page?: number; pageSize?: number; isTemplate?: boolean; category?: string }) => {
    const { data } = await api.get<AgentListResponse>('/agents', { params });
    return data;
  },

  getTemplates: async () => {
    const { data } = await api.get<{ templates: Omit<Agent, 'id' | 'createdAt' | 'updatedAt'>[] }>('/agents/templates');
    return data.templates;
  },

  get: async (id: string) => {
    const { data } = await api.get<Agent>(`/agents/${id}`);
    return data;
  },

  create: async (agent: Omit<Agent, 'id' | 'createdAt' | 'updatedAt' | 'foundryAgentId'>) => {
    const { data } = await api.post<Agent>('/agents', agent);
    return data;
  },

  update: async (id: string, updates: Partial<Agent>) => {
    const { data } = await api.put<Agent>(`/agents/${id}`, updates);
    return data;
  },

  delete: async (id: string) => {
    await api.delete(`/agents/${id}`);
  },

  invoke: async (id: string, input: string, context?: Record<string, unknown>) => {
    const { data } = await api.post<{ output: string; tokensUsed?: number }>(`/agents/${id}/invoke`, {
      input,
      context,
    });
    return data;
  },

  // Stateful multi-turn chat: loads prior conversation history + uploaded-file
  // context server-side and persists both messages. Auto-creates a session when
  // sessionId is omitted and returns the id to adopt. Use this for interactive
  // agent conversations; `invoke` above is the stateless single-shot endpoint.
  chat: async (id: string, message: string, sessionId?: string, context?: Record<string, unknown>) => {
    const { data } = await api.post<{
      sessionId: string;
      sessionCreated: boolean;
      response: string;
      usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
      toolCalls?: { tool: string }[];
      durationMs?: number;
    }>(`/agents/${id}/chat`, { message, sessionId, context });
    return data;
  },

  // --- Dynamic Model Selection ---
  listModels: async () => {
    const { data } = await api.get<Record<string, ModelDeployment[]>>('/agents/models');
    return data;
  },

  // --- Version History ---
  listVersions: async (agentId: string, params?: { page?: number; pageSize?: number }) => {
    const { data } = await api.get<{ versions: AgentVersion[]; total: number; currentVersion: number }>(
      `/agents/${agentId}/versions`, { params }
    );
    return data;
  },

  getVersion: async (agentId: string, version: number) => {
    const { data } = await api.get<AgentVersion>(`/agents/${agentId}/versions/${version}`);
    return data;
  },

  restoreVersion: async (agentId: string, version: number) => {
    const { data } = await api.post<{ agent: Agent; restoredFromVersion: number; newVersion: number }>(
      `/agents/${agentId}/versions/${version}/restore`
    );
    return data;
  },

  // --- Test Cases ---
  listTestCases: async (agentId: string, params?: { page?: number; pageSize?: number }) => {
    const { data } = await api.get<{ testCases: TestCase[]; total: number }>(
      `/agents/${agentId}/tests`, { params }
    );
    return data;
  },

  createTestCase: async (agentId: string, testCase: { name: string; input: string; description?: string; expectedOutput?: string; matchType?: string; tags?: string[] }) => {
    const { data } = await api.post<TestCase>(`/agents/${agentId}/tests`, testCase);
    return data;
  },

  updateTestCase: async (agentId: string, testId: string, updates: Partial<TestCase>) => {
    const { data } = await api.put<TestCase>(`/agents/${agentId}/tests/${testId}`, updates);
    return data;
  },

  deleteTestCase: async (agentId: string, testId: string) => {
    await api.delete(`/agents/${agentId}/tests/${testId}`);
  },

  // --- Test Execution ---
  runTest: async (agentId: string, testId: string) => {
    const { data } = await api.post<TestRun>(`/agents/${agentId}/tests/${testId}/run`);
    return data;
  },

  runAllTests: async (agentId: string) => {
    const { data } = await api.post<TestSuiteRun>(`/agents/${agentId}/tests/run-all`);
    return data;
  },

  // --- Test Results ---
  listTestRuns: async (agentId: string, params?: { testCaseId?: string; page?: number; pageSize?: number }) => {
    const { data } = await api.get<{ testRuns: TestRun[]; total: number }>(
      `/agents/${agentId}/tests/runs`, { params }
    );
    return data;
  },

  listTestSuiteRuns: async (agentId: string, params?: { page?: number; pageSize?: number }) => {
    const { data } = await api.get<{ suiteRuns: TestSuiteRun[]; total: number }>(
      `/agents/${agentId}/tests/suites`, { params }
    );
    return data;
  },
};

// User API
export const userApi = {
  getProfile: async () => {
    const { data } = await api.get<UserProfile>('/users/me');
    return data;
  },

  updateProfile: async (updates: { displayName?: string; preferences?: Partial<UserProfile['preferences']> }) => {
    const { data } = await api.put<UserProfile>('/users/me', updates);
    return data;
  },

  listAll: async (params?: { page?: number; pageSize?: number }) => {
    const { data } = await api.get<{ users: UserProfile[]; total: number; page: number; pageSize: number }>('/users', { params });
    return data;
  },

  updateRole: async (userId: string, role: 'creator' | 'poweruser' | 'member') => {
    const { data } = await api.put<UserProfile>(`/users/${userId}/role`, { role });
    return data;
  },
};

// Health check
export const healthApi = {
  check: async () => {
    const { data } = await api.get<{ status: string; timestamp: string; version: string }>('/health');
    return data;
  },
  aggregated: async () => {
    const { data } = await api.get<{
      status: 'ok' | 'degraded' | 'down';
      timestamp: string;
      checks: Record<string, { status: string; latencyMs?: number; detail?: string }>;
      uptimeSeconds: number;
    }>('/mgmt/health');
    return data;
  },
};

// Admin observability API
export const adminApi = {
  getInvocations: async (params?: { agentId?: string; status?: string; limit?: number; offset?: number }) => {
    const { data } = await api.get<{
      invocations: Array<{
        id: string;
        agentId: string;
        agentName: string;
        userId: string;
        sessionId?: string;
        model: string;
        mode: string;
        status: string;
        errorMessage?: string;
        inputTokens: number;
        outputTokens: number;
        toolRounds: number;
        toolCalls?: Array<{ tool: string; durationMs?: number }>;
        historyMessages: number;
        historyChars: number;
        truncated: boolean;
        durationMs: number;
        timestamp: string;
      }>;
      total: number;
      hasMore: boolean;
    }>('/mgmt/invocations', { params });
    return data;
  },

  getAuditLog: async (params?: { toolName?: string; status?: string; limit?: number; offset?: number }) => {
    const { data } = await api.get<{
      entries: Array<{
        id: string;
        tenantId: string;
        tenantName: string;
        method: string;
        toolName?: string;
        status: string;
        errorMessage?: string;
        durationMs: number;
        timestamp: string;
      }>;
      total: number;
    }>('/mgmt/audit-log', { params });
    return data;
  },

  getTokenUsage: async (hours: number = 24) => {
    const { data } = await api.get<{
      byAgent: Array<{
        agentId: string;
        agentName: string;
        invocations: number;
        totalInputTokens: number;
        totalOutputTokens: number;
        errors: number;
      }>;
      totalInputTokens: number;
      totalOutputTokens: number;
      period: { from: string; to: string };
    }>('/mgmt/token-usage', { params: { hours } });
    return data;
  },
};

// Task API
export { taskApi } from './taskApi';

export default api;
